import { useState, type ComponentProps, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CheckCheck, RefreshCw, XCircle } from "lucide-react";
import { Button, ConfirmModal, HoverPopover } from "@/components/ui";
import {
  HandshakeConfirmBookingModal,
  HandshakeCancelSessionModal,
  SessionStatusBadge,
  ChannelBadge,
} from "@/components/handshake";
import type { HandshakeConfirmPayload } from "@/components/handshake/handshakeConfirmTypes";
import {
  ActivityAvailabilityMode,
  type RegionCode,
} from "@/types";
import { visitAtIsoToDateInput } from "@/components/handshake/visitAt";
import {
  overCapacityDetails,
  type OverCapacityDetails,
} from "@/components/handshake/confirmSlots";
import { useHandshakeSessionActions } from "@/hooks/useHandshakeSessionActions";
import {
  useAdminHandshakeSessionDetail,
  useSellerHandshakeSessionDetail,
} from "@/hooks/useHandshakeSessionDetail";
import { activityService } from "@/services/activityService";
import { DetailSectionTitle } from "@/components/ui";
import { cn } from "@/lib/utils";

/** Booking-derived fields so list CTAs can skip GET /handshake/sessions/{id}. */
export interface HandshakePendingConfirmContext {
  visitAt: string;
  timeSlot: string;
  activityRegionCode: RegionCode;
  availabilityMode: ActivityAvailabilityMode;
}

interface BookingSellerHandshakeActionsProps {
  sessionId: string;
  canInteract: boolean;
  activityId: string;
  partySize: number;
  admin?: boolean;
  compact?: boolean;
  /** Full-width reopen button for chat card footer (screenshot layout). */
  chatFooter?: boolean;
  /**
   * When set, treat the session as PENDING and skip the session detail fetch.
   * Use on list cards (and detail when session is already loaded).
   */
  pendingConfirm?: HandshakePendingConfirmContext;
}

interface OverCapacityConfirm {
  payload: HandshakeConfirmPayload;
  details: OverCapacityDetails;
}

function HandshakeDecisionButton({
  hint,
  placement = "below",
  children,
  ...buttonProps
}: {
  hint: string;
  placement?: "above" | "below";
  children: ReactNode;
} & ComponentProps<typeof Button>) {
  return (
    <HoverPopover content={hint} placement={placement}>
      <Button {...buttonProps}>{children}</Button>
    </HoverPopover>
  );
}

async function fetchOverCapacityDetails(
  activityId: string,
  payload: HandshakeConfirmPayload,
  partySize: number,
  activityRegionCode: RegionCode,
): Promise<OverCapacityDetails | null> {
  try {
    const date =
      visitAtIsoToDateInput(payload.visit_at, activityRegionCode) ??
      payload.visit_at.slice(0, 10);
    const availability = await activityService.getAvailability(activityId, {
      date_from: date,
      date_to: date,
      people_count: partySize,
      include_all_schedule_slots: true,
    });
    return overCapacityDetails(availability.days, date, payload.time_slot);
  } catch (error) {
    console.error("Failed to pre-check slot availability:", error);
    return null;
  }
}

export function BookingSellerHandshakeActions({
  sessionId,
  canInteract,
  activityId,
  partySize,
  admin = false,
  compact = false,
  chatFooter = false,
  pendingConfirm,
}: BookingSellerHandshakeActionsProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation(["bookings", "handshakePages", "common"]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [overCapacityConfirm, setOverCapacityConfirm] =
    useState<OverCapacityConfirm | null>(null);
  const [isCheckingCapacity, setIsCheckingCapacity] = useState(false);

  const skipSessionFetch = pendingConfirm != null;
  const sellerSessionQuery = useSellerHandshakeSessionDetail(
    sessionId,
    !admin && !skipSessionFetch,
  );
  const adminSessionQuery = useAdminHandshakeSessionDetail(
    sessionId,
    admin && !skipSessionFetch,
  );
  const session = skipSessionFetch
    ? null
    : admin
      ? adminSessionQuery.data
      : sellerSessionQuery.data;

  const { cancelSession, confirmSession, reopenSession, isCancelling, isConfirming, isReopening } =
    useHandshakeSessionActions(sessionId, {
      admin,
      refreshBookings: true,
    });

  const sectionClassName = cn(
    chatFooter
      ? "w-full"
      : compact
        ? "flex w-full flex-col gap-2 sm:contents"
        : "mt-4 border-t border-surface-100 pt-4",
  );
  const showSessionMeta = !compact && session != null;

  const requestedVisitAt =
    pendingConfirm?.visitAt ?? session?.context.visit_at ?? null;
  const requestedTimeSlot =
    pendingConfirm?.timeSlot ?? session?.context.time_slot ?? null;
  const availabilityMode =
    pendingConfirm?.availabilityMode ?? session?.context.availability_mode;
  const activityRegionCode =
    pendingConfirm?.activityRegionCode ?? session?.context.activity_region_code;

  const isPending = skipSessionFetch || session?.status === "PENDING";
  const isConfirmed = !skipSessionFetch && session?.status === "CONFIRMED";

  if (!skipSessionFetch && !session) {
    return null;
  }

  if (isConfirmed) {
    if (!canInteract) {
      return null;
    }

    return (
      <div className={sectionClassName}>
        <Button
          variant={chatFooter ? "outline" : "ghost"}
          size={chatFooter ? "md" : compact ? "sm" : "md"}
          className={cn(
            chatFooter
              ? "w-full"
              : cn(
                  "text-surface-600 hover:text-surface-900",
                  compact ? "w-full justify-start px-0 sm:w-auto" : "mt-1",
                ),
          )}
          leftIcon={
            chatFooter ? <RefreshCw className="h-4 w-4" /> : undefined
          }
          disabled={isReopening}
          onClick={() => setShowReopenConfirm(true)}
        >
          {isReopening
            ? t("handshakePages:reopenSessionBusy")
            : t("handshakePages:reopenSession")}
        </Button>
        <ConfirmModal
          closeLabel={tCommon("close")}
          cancelText={tCommon("cancel")}
          isOpen={showReopenConfirm}
          onClose={() => setShowReopenConfirm(false)}
          onConfirm={() => {
            void reopenSession().then((ok) => {
              if (ok) setShowReopenConfirm(false);
            });
          }}
          title={t("handshakePages:reopenSessionConfirmTitle")}
          message={t("handshakePages:reopenSessionConfirmMessage")}
          confirmText={t("handshakePages:reopenSessionConfirm")}
          isLoading={isReopening}
        />
      </div>
    );
  }

  if (!isPending || activityRegionCode == null || availabilityMode == null) {
    return null;
  }

  const executeConfirm = async (payload: HandshakeConfirmPayload) => {
    const ok = await confirmSession(payload);
    if (ok) {
      setShowConfirmModal(false);
      setOverCapacityConfirm(null);
    }
  };

  const handleConfirm = async (payload: HandshakeConfirmPayload) => {
    if (
      payload.time_selection_mode === "SCHEDULED_SLOT" &&
      availabilityMode === ActivityAvailabilityMode.SCHEDULED &&
      activityId &&
      partySize > 0
    ) {
      setIsCheckingCapacity(true);
      try {
        const details = await fetchOverCapacityDetails(
          activityId,
          payload,
          partySize,
          activityRegionCode,
        );
        if (details) {
          setShowConfirmModal(false);
          setOverCapacityConfirm({ payload, details });
          return;
        }
      } finally {
        setIsCheckingCapacity(false);
      }
    }

    setShowConfirmModal(false);
    await executeConfirm(payload);
  };

  const confirmBusy = isConfirming || isCheckingCapacity;

  return (
    <div
      className={cn(
        compact
          ? "w-full sm:contents"
          : "mt-4 space-y-3 border-t border-surface-100 pt-4",
      )}
    >
      {!compact ? (
        <DetailSectionTitle>
          {t("bookings:detail.handshakeActions")}
        </DetailSectionTitle>
      ) : null}
      {showSessionMeta && session ? (
        <div className="flex flex-wrap items-center gap-2">
          <SessionStatusBadge status={session.status} size="sm" />
          <ChannelBadge channel={session.customer_channel} />
        </div>
      ) : null}
      {canInteract ? (
        compact ? (
          <div className="flex w-full flex-row gap-2 sm:contents">
            <HandshakeDecisionButton
              hint={t("handshakePages:rejectBookingHint")}
              variant="outline"
              size="sm"
              className="min-w-0 flex-1 whitespace-nowrap text-error-600 hover:bg-error-50 sm:w-auto sm:flex-none"
              leftIcon={<XCircle className="h-3.5 w-3.5" />}
              onClick={() => setShowCancelConfirm(true)}
              disabled={isCancelling}
            >
              {isCancelling
                ? t("handshakePages:rejectBookingBusy")
                : t("handshakePages:rejectBooking")}
            </HandshakeDecisionButton>
            <HandshakeDecisionButton
              hint={t("handshakePages:acceptBookingHint")}
              variant="accent"
              size="sm"
              className="min-w-0 flex-1 whitespace-nowrap sm:w-auto sm:flex-none"
              leftIcon={<CheckCheck className="h-3.5 w-3.5" />}
              onClick={() => setShowConfirmModal(true)}
              disabled={confirmBusy}
            >
              {confirmBusy
                ? t("handshakePages:acceptBookingBusy")
                : t("handshakePages:acceptBooking")}
            </HandshakeDecisionButton>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-1">
            <HandshakeDecisionButton
              hint={t("handshakePages:rejectBookingHint")}
              placement="above"
              variant="outline"
              size="md"
              className="h-auto min-h-9 w-full whitespace-normal px-3 py-2 text-center text-sm leading-snug text-error-600 hover:bg-error-50 hover:text-error-700"
              onClick={() => setShowCancelConfirm(true)}
              disabled={isCancelling}
            >
              <span className="inline-flex max-w-full items-center justify-center gap-2">
                <XCircle className="h-4 w-4 shrink-0" />
                <span className="min-w-0">
                  {isCancelling
                    ? t("handshakePages:rejectBookingBusy")
                    : t("handshakePages:rejectBooking")}
                </span>
              </span>
            </HandshakeDecisionButton>
            <HandshakeDecisionButton
              hint={t("handshakePages:acceptBookingHint")}
              placement="above"
              variant="accent"
              size="md"
              className="h-auto min-h-9 w-full whitespace-normal px-3 py-2 text-center text-sm leading-snug"
              onClick={() => setShowConfirmModal(true)}
              disabled={confirmBusy}
            >
              <span className="inline-flex max-w-full items-center justify-center gap-2">
                <CheckCheck className="h-4 w-4 shrink-0" />
                <span className="min-w-0 text-balance">
                  {confirmBusy
                    ? t("handshakePages:acceptBookingBusy")
                    : t("handshakePages:acceptBooking")}
                </span>
              </span>
            </HandshakeDecisionButton>
          </div>
        )
      ) : null}

      {showConfirmModal ? (
        <HandshakeConfirmBookingModal
          key={requestedVisitAt ?? "no-request"}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirm}
          requestedVisitAt={requestedVisitAt}
          requestedTimeSlot={requestedTimeSlot}
          activityRegionCode={activityRegionCode}
          activityId={activityId}
          availabilityMode={availabilityMode}
          partySize={partySize}
          isLoading={confirmBusy}
        />
      ) : null}

      <HandshakeCancelSessionModal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        cancelSession={cancelSession}
        isLoading={isCancelling}
        intent="rejectBooking"
      />

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        isOpen={overCapacityConfirm != null}
        onClose={() => {
          setOverCapacityConfirm(null);
          setShowConfirmModal(true);
        }}
        onConfirm={() => {
          if (overCapacityConfirm) {
            void executeConfirm(overCapacityConfirm.payload);
          }
        }}
        title={t("handshakePages:confirmOverCapacityTitle")}
        message={t("handshakePages:confirmOverCapacityMessage", {
          maxCapacity: overCapacityConfirm?.details.maxCapacity ?? 0,
          bookedCount: overCapacityConfirm?.details.bookedCount ?? 0,
          availableSpots: overCapacityConfirm?.details.availableSpots ?? 0,
          partySize,
        })}
        confirmText={t("handshakePages:confirmOverCapacityConfirm")}
        isLoading={isConfirming}
      />
    </div>
  );
}
